import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Spinner from "../../components/Spinner/carregando";
import tableStyles from "./IN1888.module.css";
import styles from "./enviados.module.css";
import ui from "../../styles/ui.module.css";

const formatCurrencyBRL = (value = 0) => {
  const resolved = Number(value) || 0;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(resolved);
};

const formatUSDTQuant = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });

const formatUsdtFixed = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });

const parseRowDate = (value) => {
  if (!value) return null;
  if (value.includes("/")) {
    const [day, month, year] = value.split("/");
    if (!year) return null;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const normalized = new Date(value);
  return Number.isNaN(normalized.getTime()) ? null : normalized;
};

const formatDateForDisplay = (value) => {
  const date = parseRowDate(value);
  if (!date) return value || "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const renderCell = (value) => {
  const label = value === null || value === undefined ? "—" : String(value);
  return (
    <span className={tableStyles.cellText} title={label}>
      {label}
    </span>
  );
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

export default function EnviadosModule() {
  const [mesRef, setMesRef] = useState(getCurrentMonth);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [estoque, setEstoque] = useState({
    compraUsdt: 0,
    vendaUsdt: 0,
    saldoUsdt: 0,
    compraBrl: 0,
    vendaBrl: 0,
  });
  const [selectedFileId, setSelectedFileId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [activeTab, setActiveTab] = useState("enviados");
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const fileInputRef = useRef(null);

  const loadAll = async (activeFlag) => {
    setIsLoading(true);
    try {
      const requestParams = mesRef ? { params: { mes: mesRef } } : undefined;
      const [filesRes, consolidadoRes, estoqueRes] = await Promise.all([
        api.get("/in1888/sent/files", requestParams),
        api.get("/in1888/sent/consolidado", requestParams),
        api.get("/in1888/sent/estoque", requestParams),
      ]);
      if (activeFlag && !activeFlag.current) return;
      setFiles(filesRes?.data ?? []);
      setRows(consolidadoRes?.data ?? []);
      setEstoque(
        estoqueRes?.data ?? {
          compraUsdt: 0,
          vendaUsdt: 0,
          saldoUsdt: 0,
          compraBrl: 0,
          vendaBrl: 0,
        }
      );
    } catch (error) {
      if (activeFlag && !activeFlag.current) return;
      toast.error("Não foi possível carregar os enviados IN 1888.");
      console.error("Erro ao carregar enviados IN 1888:", error);
    } finally {
      if (!activeFlag || activeFlag.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const activeRef = { current: true };
    loadAll(activeRef);
    return () => {
      activeRef.current = false;
    };
  }, [mesRef]);

  useEffect(() => {
    if (activeTab !== "historico") return;
    let active = true;
    const loadMonthly = async () => {
      setMonthlyLoading(true);
      try {
        const res = await api.get("/in1888/stock/monthly");
        if (!active) return;
        setMonthlyRows(res?.data ?? []);
      } catch (error) {
        if (!active) return;
        toast.error("Não foi possível carregar histórico mensal.");
        console.error("Erro ao carregar histórico mensal IN1888:", error);
      } finally {
        if (active) setMonthlyLoading(false);
      }
    };
    loadMonthly();
    return () => {
      active = false;
    };
  }, [activeTab]);

  const handleImportSent = async () => {
    if (!selectedFiles.length) {
      toast.error("Selecione um arquivo .txt");
      return;
    }
    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));
    setIsImporting(true);
    try {
      await api.post("/in1888/sent/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Importado com sucesso");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      await loadAll();
    } catch (error) {
      console.error("Erro ao importar IN 1888 enviado:", error?.response?.data || error);
      if (error?.response?.status === 409) {
        toast.warn("Arquivo já foi importado.");
        await loadAll();
        return;
      }
      const msg = error?.response?.data?.message || "Erro ao importar";
      toast.error(msg);
    } finally {
      setIsImporting(false);
    }
  };

  const handleDeleteSentFile = async (id) => {
    if (!window.confirm("Deseja excluir este arquivo importado?")) return;
    try {
      await api.delete(`/in1888/sent/files/${id}`);
      toast.success("Arquivo excluído.");
      await loadAll();
    } catch (error) {
      toast.error("Não foi possível excluir o arquivo.");
      console.error("Erro ao excluir IN 1888 enviado:", error);
    }
  };

  const handleCleanupAll = async () => {
    const ok = window.confirm(
      "Isso vai apagar TODOS os enviados IN1888 (files, items e totais). Deseja continuar?"
    );
    if (!ok) return;
    try {
      await api.delete("/in1888/sent/cleanup");
      toast.success("Tabelas limpas com sucesso.");
      await loadAll();
      if (activeTab === "historico") {
        const res = await api.get("/in1888/stock/monthly");
        setMonthlyRows(res?.data ?? []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Falha ao limpar tabelas.");
      console.error("Erro ao limpar IN1888:", error);
    }
  };

  const normalizedRows = useMemo(() => {
    const filtered = selectedFileId
      ? rows.filter((row) => String(row.FILE_ID ?? row.file_id) === String(selectedFileId))
      : rows;
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.DATA || a.data || 0).getTime();
      const dateB = new Date(b.DATA || b.data || 0).getTime();
      if (dateA !== dateB) return dateA - dateB;
      const regA = String(a.REG || "").trim();
      const regB = String(b.REG || "").trim();
      return regA.localeCompare(regB);
    });
  }, [rows, selectedFileId]);

  const safeEstoque = useMemo(() => {
    const compraUsdt = Number(estoque.compraUsdt || 0);
    const vendaUsdt = Number(estoque.vendaUsdt || 0);
    const compraBrl = Number(estoque.compraBrl || 0);
    const vendaBrl = Number(estoque.vendaBrl || 0);
    const saldoUsdt =
      estoque.saldoUsdt !== undefined && estoque.saldoUsdt !== null
        ? Number(estoque.saldoUsdt || 0)
        : compraUsdt - vendaUsdt;
    return { compraUsdt, vendaUsdt, saldoUsdt, compraBrl, vendaBrl };
  }, [estoque]);

  return (
    <div className={styles.wrapper}>
      <Spinner isLoading={isImporting} message="Importando arquivo..." />
      <header className={styles.header}>
        <div>
          {/*<h3 className={styles.title}>IN1888 Enviados</h3>*/}
          <p className={styles.subtitle}>Arquivos TXT enviados ao Coleta Nacional.</p>
        </div>
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${ui.butdesktop} ${activeTab === "enviados" ? styles.tabActive : styles.tab}`}
            onClick={() => setActiveTab("enviados")}
          >
            Enviados
          </button>
          <button
            type="button"
            className={`${ui.butdesktop} ${activeTab === "historico" ? styles.tabActive : styles.tab}`}
            onClick={() => setActiveTab("historico")}
          >
            EstoqueUSDT
          </button>
        </div>
        <div className={styles.controlsRow}>
          <div className={styles.control}>
            <label className={styles.label} htmlFor="mes-ref">
              Mês
            </label>
            <input
              id="mes-ref"
              type="month"
              value={mesRef}
              className={styles.input}
              onChange={(event) => setMesRef(event.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => loadAll()}
            disabled={isLoading}
          >
            Atualizar
          </button>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={handleCleanupAll}
            disabled={isLoading}
          >
            Limpar tabelas
          </button>
        </div>
      </header>

      {activeTab === "historico" && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>Estoque USDT (Histórico)</h4>
          <div className={styles.tableWrapper}>
            <table className={`${tableStyles.table} ${tableStyles.tableAuto} ${tableStyles.noWrap}`}>
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className={styles.numCell}>Compra USDT</th>
                  <th className={styles.numCell}>Venda USDT</th>
                  <th className={styles.numCell}>Net USDT</th>
                  <th className={styles.numCell}>Saldo Acumulado (USDT)</th>
                </tr>
              </thead>
              <tbody>
                {monthlyRows.map((row) => (
                  <tr key={row.MES_REF ?? row.mes_ref}>
                    <td>{renderCell(row.MES_REF ?? row.mes_ref ?? "—")}</td>
                    <td className={styles.numCell}>
                      {renderCell(formatUsdtFixed(row.TOTAL_COMPRA_USDT || 0))}
                    </td>
                    <td className={styles.numCell}>
                      {renderCell(formatUsdtFixed(row.TOTAL_VENDA_USDT || 0))}
                    </td>
                    <td className={styles.numCell}>
                      {renderCell(formatUsdtFixed(row.NET_USDT || 0))}
                    </td>
                    <td className={styles.numCell}>
                      {renderCell(formatUsdtFixed(row.CUM_USDT || 0))}
                    </td>
                  </tr>
                ))}
                {!monthlyRows.length && !monthlyLoading && (
                  <tr>
                    <td colSpan={5} className={styles.emptyState}>
                      Nenhum dado mensal disponível.
                    </td>
                  </tr>
                )}
                {monthlyLoading && (
                  <tr>
                    <td colSpan={5} className={styles.emptyState}>
                      Carregando histórico...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "enviados" && (
        <>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Importar TXT enviado</h4>
        <div className={styles.importRow}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            className={styles.fileInput}
            multiple
            onChange={(event) => {
              setSelectedFiles(Array.from(event.target.files || []));
            }}
          />
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleImportSent}
            disabled={isLoading}
          >
            Importar
          </button>
          {isLoading && <span className={styles.helper}>Carregando...</span>}
        </div>
        {selectedFiles.length > 0 && (
          <div className={styles.fileIndicator}>
            <span>
              {selectedFiles.length === 1 ? "Arquivo selecionado:" : "Arquivos selecionados:"}{" "}
              <b>{selectedFiles.map((file) => file.name).join(", ")}</b>{" "}
              ({(selectedFiles.reduce((acc, file) => acc + file.size, 0) / 1024).toFixed(1)} KB)
            </span>
            <button
              type="button"
              className={styles.fileClearBtn}
              onClick={() => {
                setSelectedFiles([]);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              aria-label="Remover arquivo selecionado"
            >
              ✕
            </button>
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Resumo / Estoque</h4>
        <div className={styles.cards}>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Compras USDT</span>
            <strong>{formatUSDTQuant(safeEstoque.compraUsdt)}</strong>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Vendas USDT</span>
            <strong>{formatUSDTQuant(safeEstoque.vendaUsdt)}</strong>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Saldo USDT</span>
            <strong>{formatUSDTQuant(safeEstoque.saldoUsdt)}</strong>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Compras BRL</span>
            <strong>{formatCurrencyBRL(safeEstoque.compraBrl)}</strong>
          </div>
          <div className={styles.card}>
            <span className={styles.cardLabel}>Vendas BRL</span>
            <strong>{formatCurrencyBRL(safeEstoque.vendaBrl)}</strong>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Arquivos importados</h4>
          <div className={styles.control}>
            <label className={styles.label} htmlFor="file-filter">
              Filtrar por arquivo
            </label>
            <select
              id="file-filter"
              className={styles.input}
              value={selectedFileId}
              onChange={(event) => setSelectedFileId(event.target.value)}
            >
              <option value="">Todos</option>
              {files.map((file) => (
                <option key={file.ID ?? file.id} value={file.ID ?? file.id}>
                  {file.FILENAME || "Arquivo"}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.tableWrapper}>
          <table className={`${tableStyles.table} ${tableStyles.tableAuto} ${tableStyles.noWrap}`}>
            <thead>
              <tr>
                <th>Data</th>
                <th>Arquivo</th>
                <th>Registros</th>
                <th>Compra USDT</th>
                <th>Venda USDT</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.ID ?? file.id}>
                  <td>{renderCell(file.CREATED_AT ?? file.created_at ?? "—")}</td>
                  <td className={tableStyles.nameCell} title={file.FILENAME || ""}>
                    {renderCell(file.FILENAME || "—")}
                  </td>
                  <td>{renderCell(file.ITEM_COUNT ?? file.item_count ?? 0)}</td>
                  <td>{renderCell(formatUSDTQuant(file.TOTAL_COMPRA_USDT || 0))}</td>
                  <td>{renderCell(formatUSDTQuant(file.TOTAL_VENDA_USDT || 0))}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      onClick={() => handleDeleteSentFile(file.ID ?? file.id)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {!files.length && (
                <tr>
                  <td colSpan={6} className={styles.emptyState}>
                    Nenhum arquivo encontrado para o mês selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Consolidado</h4>
        <div className={styles.tableWrapper}>
          <table className={`${tableStyles.table} ${tableStyles.tableAuto} ${tableStyles.noWrap}`}>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Data</th>
                <th>Nome</th>
                <th>CPF</th>
                <th>Valor BRL</th>
                <th>Quant USDT</th>
                <th>COD</th>
                <th>Arquivo</th>
              </tr>
            </thead>
            <tbody>
              {normalizedRows.map((item) => (
                <tr key={item.ID ?? item.id}>
                  <td>
                    {renderCell(
                      String(item.REG || "").trim() === "0110" ? "COMPRA" : "VENDA"
                    )}
                  </td>
                  <td>{renderCell(formatDateForDisplay(item.DATA ?? item.data))}</td>
                  <td className={tableStyles.nameCell} title={item.NOME || ""}>
                    {renderCell(item.NOME || "—")}
                  </td>
                  <td>{renderCell(item.CPF || "—")}</td>
                  <td>{renderCell(formatCurrencyBRL(item.VALORBRL || 0))}</td>
                  <td>{renderCell(formatUSDTQuant(item.QUANT || 0))}</td>
                  <td>{renderCell(item.COD ?? "—")}</td>
                  <td className={tableStyles.nameCell} title={item.FILENAME || ""}>
                    {renderCell(item.FILENAME || "—")}
                  </td>
                </tr>
              ))}
              {!normalizedRows.length && (
                <tr>
                  <td colSpan={8} className={styles.emptyState}>
                    Nenhum registro consolidado encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
        </>
      )}
    </div>
  );
}
